import React from 'react';
import { Button } from 'antd';
import _, { forEach } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import buttons from 'helpers/styles/components/buttons.module.sass';
import { UserDetails } from 'api/User/types';
import userRequests from 'api/PersonalInfo/requests';
import { UserRedux } from 'redux/User/types';
import s from './styles.module.sass';
import { EmployeeOccupationNames } from 'helpers/constants/Occupations/types';
import defaultAvatar from "helpers/icons/NavBarMenu/Logo.svg";
import jsPDF from "jspdf";
import { PALETTE, FONTS, RGB, LEGEND, LINE_WIDTH, CONTACTS, ATTITUDE_COLORS } from './constants';
import SKILL_CATEGORIES from 'helpers/constants/Skills/Categories';
import SKILL_EXPERIENCES from 'helpers/constants/Skills/Experience';
import ENGLISH_KNOWLEDGE_STATES from 'helpers/constants/Skills/EnglishKnowledgeStates';
import { EnglishLevelEnum } from 'helpers/types/_common';
import { AttitudesEnum } from 'helpers/types/Skills';


interface DownloadSkillsetButtonProps {
    user: UserDetails | UserRedux;
}


const HEADER_HEIGHT = 12;
const SUBHEADER_HEIGHT = 15;
const LEGEND_HEIGHT = 15;
const TAG_BASE_OFFSET = 3;
const MAIN_TABLE_X_OFFSET = 202;
const MAIN_LINE_END_X_OFFSET = 10;


const isOverlappingFooter = (pdf: jsPDF, offsetY: number) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  const maxY = pageHeight - SUBHEADER_HEIGHT;

  return offsetY > maxY;
} 


const loadImageAsBase64 = (url: string, circle: boolean = false, size = 0) =>
  new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      if (size) {
        canvas.width = size;
        canvas.height = size;
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }

      const ctx = canvas.getContext("2d");
      if (_.isNil(ctx)) {
        reject(new Error("Canvas 2D context is null"));
        return
      }
      if (circle) {
        const circleSize = 256;
        canvas.width = circleSize;
        canvas.height = circleSize;
        ctx.beginPath();
        ctx.arc(circleSize / 2, circleSize / 2, circleSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, 0, 0, circleSize, circleSize);
      } else {
        if (size) {ctx.drawImage(img, 0, 0, size, size)} else {ctx.drawImage(img, 0, 0)};
      }

      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = reject;
    img.src = url;
  }
);

const attitudeToColor = (attitude: keyof typeof AttitudesEnum | null) => {
  if (_.isNil(attitude)) { return PALETTE.TAG_NOT_SPECIFIED_BORDER }
  return _.get(ATTITUDE_COLORS, attitude, PALETTE.TAG_NOT_SPECIFIED_BORDER)
};

const DrawHeader = async (pdf: jsPDF, pageNumber: number, totalPages: number) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const textX = pageWidth - 60.25;
  const textY = 7;
  const underlineY = textY + 2;
  
  pdf.setFillColor(...PALETTE.HEADER_BACKGROUND);
  pdf.rect(0, 0, pageWidth, HEADER_HEIGHT, "F");
  const logo = await loadImageAsBase64("/icons/logo-icon-name.png");
  pdf.addImage(logo, "PNG", 5, 3, 25, 7); 
  
  pdf.setFont(...FONTS.HEADER);
  pdf.setTextColor(...PALETTE.FONT_REVERSE);
  pdf.setFontSize(9);
  pdf.text("Engineer Skillset", pageWidth - 60, 7);
  pdf.setDrawColor(...PALETTE.HEADER_UNDERLINE);
  pdf.setLineWidth(LINE_WIDTH);
  pdf.line(textX, underlineY, pageWidth - 34, underlineY);
  
  pdf.text(`${pageNumber} / ${totalPages}`, pageWidth - 20, 7);
}

const DrawSubHeader = async (pdf: jsPDF, user: UserDetails | UserRedux) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const avatarTextX = pageWidth - 190;
  const textY = 19;
  const textPaddingY = 4;
  const contactPaddingX = 3;
  const dividerStartY = 16;
  const minimalContactsGap = 20;
  let contactsStartX = pageWidth - 110;

  pdf.setFillColor(...PALETTE.SUBHEADER_BACKGROUND);
  pdf.rect(0, HEADER_HEIGHT, pageWidth, SUBHEADER_HEIGHT, "F");
  user.avatarUrl = "";
  if (user.avatarUrl) {
    const avatar = await loadImageAsBase64(user.avatarUrl, true);
      if (avatar) {
      pdf.addImage(avatar, "PNG", 6, 13, 12, 12);
    }
  } else {
    pdf.setFillColor(...PALETTE.TAG_BACKGROUND);
    pdf.circle(13, 20, 6, "F");
    const avatar = await loadImageAsBase64(defaultAvatar, false, 512);
    pdf.addImage(avatar, "PNG", 10, 17, 6, 6);
  }
  
  pdf.setTextColor(...PALETTE.FONT_MAIN);
  pdf.setFont(...FONTS.SUBHEADER);
  pdf.setFontSize(10);
  pdf.text(_.startCase(`${user.forename} ${user.surname}`), avatarTextX, textY);
  pdf.setFontSize(9);
  pdf.setTextColor(...PALETTE.FONT_EXTRA);
  pdf.text(EmployeeOccupationNames[user.occupation], avatarTextX, textY + textPaddingY);
  pdf.setLineWidth(LINE_WIDTH);
  pdf.setDrawColor(...PALETTE.SUBHEADER_DIVIDER);
  pdf.setFont(...FONTS.SUBHEADER);

  const contactSections = Object.entries(CONTACTS);
  contactSections.forEach(([key, value], index) => {
    const text = _.get(user, key) ?? "-";
    const textWidth = _.max([pdf.getTextWidth(text), minimalContactsGap]);
    const dividerX = contactsStartX + textWidth + contactPaddingX;
    const isLast = index === contactSections.length - 1;

    pdf.setTextColor(...PALETTE.FONT_EXTRA);
    pdf.text(value, contactsStartX, textY);
    pdf.setTextColor(...PALETTE.FONT_MAIN);
    pdf.text(text, contactsStartX, textY + textPaddingY);
    if (!isLast) {
      pdf.line(dividerX, dividerStartY, dividerX, textY + textPaddingY);
    }
    contactsStartX = dividerX + contactPaddingX;
  })
}

const DrawFooter = (pdf: jsPDF) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const footerTagsHeight = pageHeight - 11;
  const footerTextsHeight = pageHeight - 6.5;
  const legendSegmentPaddingX = 6;
  const textPaddingX = 10;
  let startX = 10;
  

  pdf.setFillColor(...PALETTE.SUBHEADER_BACKGROUND);
  pdf.rect(0, pageHeight - LEGEND_HEIGHT, pageWidth, SUBHEADER_HEIGHT, "F");
  forEach(LEGEND, (segment) => {
    drawTag(pdf, "Skill", startX, footerTagsHeight, segment.color)
    const text = ` - ${segment.text}`;
    const textWidth = pdf.getTextWidth(text);

    pdf.setTextColor(...PALETTE.FONT_EXTRA);
    pdf.setFontSize(8);
    pdf.text(` - ${segment.text}`, startX + textPaddingX, footerTextsHeight);
    startX += textPaddingX + textWidth + legendSegmentPaddingX;
  })
};

const DrawCategory = (pdf: jsPDF, offsetY: number, name: string, skillset: any) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const categoryNameX = pageWidth - MAIN_TABLE_X_OFFSET;
  const tagMaxX = pageWidth - 30;
  const categoryLineEndX = pageWidth - MAIN_LINE_END_X_OFFSET;
  const expertiseBottomLineOffsetY = 10;
  const tagLineStepY = TAG_BASE_OFFSET * 3;
  let categoryY: number = 0;
  let expertiseY: number = 0;
  
  const recalcY = (reset: boolean) => {
    if (reset) {
      offsetY = HEADER_HEIGHT + 10;
    }
    categoryY = offsetY + TAG_BASE_OFFSET;
    expertiseY = categoryY + TAG_BASE_OFFSET * 2;
  };

  const drawHorizontalDivider = (y: number) => {
    pdf.setDrawColor(...PALETTE.SUBHEADER_DIVIDER);
    pdf.line(categoryNameX, y, categoryLineEndX, y);
  };

  const drawVerticalDivider = (x: number, startY: number, endY: number) => {
    pdf.line(x, startY, x, endY);
  }

  const makeTotalText = (total: number) => {
    return `${total} ${total == 1 ? "skill" : "skills"}`
  }

  const addCategoryText = () => {
    // Category name
    pdf.setTextColor(...PALETTE.FONT_MAIN);
    pdf.setFont(...FONTS.SUBHEADER);
    pdf.setFontSize(10);
    const categoryNameWidth = pdf.getTextWidth(name);
    pdf.text(name, categoryNameX, offsetY);

    // Skills total in category
    pdf.setTextColor(...PALETTE.FONT_EXTRA);
    pdf.text(makeTotalText(skillset.skillsTotal), categoryNameX + categoryNameWidth + 10, offsetY);
  };

  const addExpertiseText = (y: number, name: string, total: number) => {
    pdf.setFontSize(9);
    pdf.setTextColor(...PALETTE.FONT_EXPERTISE_LEVEL);
    pdf.text(name, categoryNameX, y);
    
    pdf.setFontSize(8);
    pdf.setTextColor(...PALETTE.FONT_EXTRA);
    pdf.text(makeTotalText(total), categoryNameX, y + TAG_BASE_OFFSET);
  };
 
  recalcY(false);  
  if (isOverlappingFooter(pdf, expertiseY + TAG_BASE_OFFSET * 2)) {
    pdf.addPage();
    recalcY(true);
  };

  addCategoryText();  
  drawHorizontalDivider(categoryY);
  
  const expertiseDividerX = categoryNameX + 25;
  const startTagX = expertiseDividerX + TAG_BASE_OFFSET;

  const sortedExperiences = _.orderBy(_.toPairs(SKILL_EXPERIENCES), [(item) => item[1].experienceId],["desc"]);
  
  _.forEach(sortedExperiences, ([key, value]) => {
    const expertiseSkillset = _.get(skillset, _.camelCase(key)); 
    if (expertiseSkillset.skillsTotal > 0) {
      let startTagY = 0; 
      let tagX = 0;
      let tagY = 0;
      const recalcTagsXY = () => {
        startTagY = expertiseY - TAG_BASE_OFFSET; 
        tagX = startTagX;
        tagY = startTagY;
      };
      recalcTagsXY();
      const expertiseTextY = (y: number) => (
        startTagY == y ? 
        startTagY + TAG_BASE_OFFSET : 
        startTagY + (y - startTagY) / 2 + TAG_BASE_OFFSET
      );

      _.forEach(expertiseSkillset.skills, (skill, i: number, col) => {
        if (isOverlappingFooter(pdf, tagY + TAG_BASE_OFFSET * 5)) {
          if (tagY != startTagY) {
            drawHorizontalDivider(tagY);
            addExpertiseText(expertiseTextY(tagY - tagLineStepY), value.downloadLabel, expertiseSkillset.skillsTotal);
            drawVerticalDivider(expertiseDividerX, categoryY, tagY);
          } else {
            drawVerticalDivider(expertiseDividerX, categoryY, tagY - 2);
          }
          
          pdf.addPage();
          recalcY(true);
          recalcTagsXY();
          drawHorizontalDivider(categoryY);
          addCategoryText();
        }
        drawTag(pdf, skill.name, tagX, tagY, attitudeToColor(skill.attitude));
        const tagWidth = pdf.getTextWidth(skill.name);
        tagX += tagWidth + 2 * TAG_BASE_OFFSET;
        if (tagX > tagMaxX && i != col.length - 1) {
          tagX = startTagX;
          tagY += tagLineStepY;
        };
      });

      addExpertiseText(expertiseTextY(tagY), value.downloadLabel, expertiseSkillset.skillsTotal);
      drawHorizontalDivider(tagY + expertiseBottomLineOffsetY);
      expertiseY = tagY + TAG_BASE_OFFSET * 5;
    }
  });

  // Vertical expertise level name and tags divider
  drawVerticalDivider(expertiseDividerX, categoryY, expertiseY - 5);
  return expertiseY + TAG_BASE_OFFSET;
};

const drawEnglishLevel = (pdf: jsPDF, offsetY: number, level: EnglishLevelEnum) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const titleX = pageWidth - MAIN_TABLE_X_OFFSET;
  const titleUnderLineEndX = pageWidth - MAIN_LINE_END_X_OFFSET;
  const totalBars = 4;
  const barWidth = 0.5;
  const barsGap = 1;
  let userReadableLevel = ENGLISH_KNOWLEDGE_STATES.find(state => state.knowledgeId == level);
  if (_.isNil(userReadableLevel)) {
     userReadableLevel = ENGLISH_KNOWLEDGE_STATES[0];
  }
  let titleUnderLineY = 0;
  let levelNameY = 0;
  let levelDescriptionY = 0;
  let barsY = 0;

  const recalcY = (reset: boolean) => {
    if (reset) {
      offsetY = HEADER_HEIGHT + 10;
    }
    titleUnderLineY = offsetY + TAG_BASE_OFFSET;
    levelNameY = titleUnderLineY + TAG_BASE_OFFSET * 2;
    levelDescriptionY = titleUnderLineY + TAG_BASE_OFFSET * 3;
    barsY = titleUnderLineY + 2;
  };
  recalcY(false);

  if (isOverlappingFooter(pdf, levelDescriptionY)) {
    pdf.addPage();
    recalcY(true);
  }

  // Title and horizontal divider
  pdf.setTextColor(...PALETTE.FONT_MAIN);
  pdf.setFont(...FONTS.SUBHEADER);
  pdf.setFontSize(10);
  pdf.text('English Knowledge', titleX, offsetY);
  pdf.setDrawColor(...PALETTE.SUBHEADER_DIVIDER);
  pdf.line(titleX, titleUnderLineY, titleUnderLineEndX, titleUnderLineY);

  // Level name and description
  const levelName = _.get(userReadableLevel, "downloadLabel", userReadableLevel.name);
  const levelDescription = _.get(userReadableLevel, "downloadDescription", userReadableLevel.descriptions);
  pdf.setFontSize(9);
  pdf.setTextColor(...PALETTE.FONT_ENGLISH_LEVEL);
  pdf.text(levelName, titleX + TAG_BASE_OFFSET * 2, levelNameY);  
  pdf.setFontSize(8);
  pdf.setTextColor(...PALETTE.FONT_EXTRA);
  pdf.text(levelDescription, titleX, levelDescriptionY);

  // "Signal" bars visualizing level
  for (let i = 1; i <= totalBars; i++) {
    const barX = titleX + (i - 1) * (barWidth + barsGap);
    const barY = barsY + (totalBars - i);

    const isActive = i <= userReadableLevel.value;
    const color = isActive ? PALETTE.ENGLISH_LEVEL_FULFILLED_CELL : PALETTE.ENGLISH_LEVEL_EMPTY_CELL;

    pdf.setFillColor(...color);
    pdf.roundedRect(barX, barY, barWidth, i, barWidth / 2, barWidth / 2, "F");
  }
};

const drawTag = (pdf: jsPDF, text: string, x: number, y: number, color: RGB) => {
  const paddingX = 2;
  const height = 6;

  pdf.setFontSize(9);
  pdf.setFont(...FONTS.SUBHEADER);

  const textWidth = pdf.getTextWidth(text) + paddingX * 2;

  pdf.setFillColor(...PALETTE.TAG_BACKGROUND);

  pdf.setDrawColor(...color);
  pdf.setLineWidth(LINE_WIDTH);
  pdf.roundedRect(
    x,
    y + 0.5,
    textWidth,
    height,
    height / 2,
    height / 2,
    "FD"
  );

  pdf.setTextColor(0, 0, 0);
  pdf.text(text, x + paddingX, y + height / 2 + 1.5);

  return textWidth;
};

const DrawDocument = async (user: UserDetails | UserRedux, skillset: any) => {
  const pdf = new jsPDF();
  let categoryOffset = HEADER_HEIGHT + SUBHEADER_HEIGHT + 10;

  _.forEach(SKILL_CATEGORIES, (value, key) => {
    const categorySkillset = _.get(skillset.skillset, _.camelCase(key));
    
    if (!_.isNil(categorySkillset)) {
      categoryOffset = DrawCategory(
      pdf,
      categoryOffset,
      value.title,
      categorySkillset,
    );
    };
  });

  drawEnglishLevel(pdf, categoryOffset, skillset.profile.englishKnowledge);

  const totalPages = pdf.getNumberOfPages();
  for (let i = 0; i < totalPages; i++) {
    pdf.setPage(i + 1);
    if (i == 0) {
      await DrawSubHeader(pdf, user);
    }
    await DrawHeader(pdf, i+ 1, totalPages);
    DrawFooter(pdf)
  }
  const currentDate = new Date();
  pdf.save(`${user.forename}.${user.surname} skillset ${currentDate.toLocaleDateString('ru-RU')}.pdf`);
}

const PdfExportButton = async (user: UserDetails | UserRedux) => {
  const skillset = await userRequests.downloadSkillsetJSON(user.userId);
  await DrawDocument(user, skillset);
};


const DownloadSkillsetPDFButton: React.FC<DownloadSkillsetButtonProps> = ({user}) => {
  return <div className={s.downloadButtonPDFWrapper}>
    <Button type="primary" className={buttons.qsButtonPrimary} onClick={async () => {
      await PdfExportButton(user)
    }}>
      <FontAwesomeIcon icon="download" />
      Download PDF
    </Button>
  </div>
};

export default DownloadSkillsetPDFButton;
